import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { type InputType, type TextAlign } from '#imports';
import type ItineraryPackageSet from '@itinerary/ItineraryPackageSet';
import { ReserveType } from '@utils/itinerary';
import { type ReserveInfo } from '@utils/types';

export const AfterOrderVehicleTypesToReserveType = {
    Flight_i: ReserveType.Flight,
    Flight_d: ReserveType.Flight,
    Train: ReserveType.Train,
    Bus: ReserveType.Bus,
    Taxi: ReserveType.Taxi,
    Hotel: ReserveType.Hotel,
};

export type EmptyListReason = {
    image: string;
    title: string;
    notFoundText: string;
    idOfElement?: string;
};

export type Suggestion = {
    reserveInfo: ReserveInfo;
    price: number;
};

/**
 * base sort by
 */
export type SortBy = {
    label: string;
    type: string;
};

/**
 * base class of FilterData
 *
 * @export
 * @interface FilterData
 */
export interface FilterData {}

export type FilterKey = string | number | symbol;

/**
 * base type for meta data in search result
 *
 * @export
 * @interface MetaData
 */
export type MetaData = {
    hasPreSell?: boolean;
    routeNotFound?: boolean;
    priceTrackEnabled?: boolean;
};

/**
 * Indicates the loading status whether it is loading, error, success, offline or none
 */
export enum LoadingStatus {
    None = 'none',
    Loading = 'loading',
    RealtimeLoading = 'realtimeLoading',
    Error = 'error',
    Success = 'success',
    Offline = 'offline',
}

//* ********************* filters */

/**
 * Pair of ID and its title
 */
export interface IdTitle {
    /**
     * ID
     *
     * @type {string}
     * @memberof IdTitle
     */
    id: string;

    /**
     * Title related to the id
     *
     * @type {string}
     * @memberof IdTitle
     */
    title: string;

    /**
     * subtitle related to id
     *
     * @type {string}
     * @memberof IdTitle
     */
    subTitle?: string;
}

export type FilterComponents =
    | 'DurationFilter'
    | 'ButtonGroupFilter'
    | 'CheckboxFilter'
    | 'PriceFilter'
    | 'RadioFilter'
    | 'TimeFilter'
    | 'StarFilter'
    | 'InputFilter';

export interface Filter {
    /**
     * Name of the filter
     *
     * @type {string}
     * @memberof Filter
     */
    name: string;

    /**
     * Title of the filter
     *
     * @type {string}
     * @memberof Filter
     */
    title: string;

    /**
     * Name of the filter component
     *
     * @type {string}
     * @memberof Filter
     */
    component: FilterComponents;

    items: IdTitle[] | null;
}

/**
 * Informations of a filter applied to trip prices
 */
export class PriceFilter implements Filter {
    /**
     * Name of the filter component (PriceFilter)
     *
     * @type {string}
     * @memberof PriceFilter
     */
    component: FilterComponents = 'PriceFilter';

    items = null;

    /**
     * Creates an instance of PriceFilter
     *
     * @param {string} name
     * @param {number} min
     * @param {number} max
     * @param {string} title
     * @memberof PriceFilter
     */
    constructor(
        public name: string,
        public min: number,
        public max: number,
        public title = 'قیمت'
    ) {}
}

/**
 * Informations of filter items checked in checkbox
 */
export class CheckboxFilter implements Filter {
    /**
     * Name of the filter component (CheckboxFilter)
     *
     * @type {string}
     * @memberof CheckboxFilter
     */
    component: FilterComponents = 'CheckboxFilter';

    /**
     * Creates an instance of CheckboxFilter
     *
     * @param {string} name
     * @param {IdTitle[]} items
     * @param {string} title
     * @memberof CheckboxFilter
     */
    constructor(
        public name: string,
        public items: IdTitle[],
        public title: string
    ) {}
}

export class InputFilter implements Filter {
    component: FilterComponents = 'InputFilter';
    items = null;

    constructor(
        public name: string,
        public title: string,
        inputProps?: {
            label?: string;
            clearable?: boolean;
            description?: string;
            icon?: IconDefinition;
            placeholder?: string;
            min?: number;
            max?: number;
            minLength?: number;
            maxLength?: number;
            type?: InputType;
            dir?: 'rtl' | 'ltr';
            align?: TextAlign;
        }
    ) {
        if (inputProps) {
            Object.assign(this, inputProps);
        }
    }
}

/**
 * Informations of filter items checked in radio
 */
export class RadioFilter implements Filter {
    /**
     * Name of the filter component (RadioFilter)
     *
     * @type {string}
     * @memberof RadioFilter
     */
    component: FilterComponents = 'RadioFilter';

    /**
     * Creates an instance of RadioFilter
     *
     * @param {string} name
     * @param {IdTitle[]} items
     * @param {string} title
     * @memberof RadioFilter
     */
    constructor(
        public name: string,
        public items: IdTitle[],
        public title: string
    ) {}
}

/**
 * Informations of filter items checked in button group filters
 */
export class ButtonGroupFilter implements Filter {
    /**
     * Name of the filter component (ButtonGroupFilter)
     *
     * @type {string}
     * @memberof ButtonGroupFilter
     */
    component: FilterComponents = 'ButtonGroupFilter';

    /**
     * Creates an instance of ButtonGroupFilter
     *
     * @param {string} name
     * @param {IdTitle[]} items
     * @param {string} title
     * @memberof ButtonGroupFilter
     */
    constructor(
        public name: string,
        public items: IdTitle[],
        public title: string
    ) {}
}

export class NetworkError extends Error {
    override message = 'Network Error';
}

export type ListErrorTypes = 'Unknown' | 'Timeout' | 'Network' | 'Disabled';
export type ListErrorMV = {
    id: string;
    type: ListErrorTypes;
    image: string;
    message: string;
    description: string;
    timeout?: Date;
};

export type AlternativeSearchSuggestion = {
    packageSet: ItineraryPackageSet;
    name: string;
    icon: IconDefinition;
    type: ReserveType;
};

export interface Filters {
    key: FilterKey;
    meta: FilterData;
}
